import { Box, Container } from "@mui/material";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useAddEditPhysicalEdition } from "../hooks/useAddEditPhysicalEdition";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useEffect, useMemo, useState } from "react";
import { ColumnsType } from "src/interfaces/Common";
import EditionFormCst from "@components/EditionFormCst";
import TeamsLimitAlertCst from "@components/TeamsLimitAlertCst";

const AddPhysicalEditions: React.FC = () => {
  const { t } = useTranslation(["physical-editions-page"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [editColumns, setEditColumns] = useState<ColumnsType | any>();
  const [activityDuration, setActivityDuration] = useState(
    locationState?.duration || 0,
  );
  const [showModalWarning, setShowModalWarning] = useState(false);

  const { columns, mutate } = useFetchDetail(
    ServicesURL.getEditions,
    undefined,
    PagesID["corporate.physical-editions"],
  );

  const { columns: sessionColumns } = useFetchDetail(
    ServicesURL.getEditions,
    undefined,
    PagesID["corporate.physical-editions.add"],
  );

  const { dataDetail: activityDetail, isLoading: isLoadingActivity } =
    useFetchDetail(
      locationState?.id
        ? `${ServicesURL.getActivities.replace(":idCorporate", `${id}`)}`
        : "",
      locationState?.id || "",
      PagesID["catalog.activities"],
    );

  const { trigger } = usePostDetail(
    ServicesURL.postEditions + `/physical?corporateId=${id}`,
    { onError: () => {} },
  );

  const dataLocation = useMemo(
    () =>
      locationState
        ? {
            learning_objects: {
              id: locationState.id,
              label: locationState.title,
            },
            site: undefined,
            rooms: undefined,
          }
        : undefined,
    [locationState],
  );

  const {
    onSubmit,
    openConfirmStatus,
    setOpenConfirmStatus,
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetSessionAssociations,
    setSiteAssociation,
    setRoomAssociation,
    setFacultiesAssociation,
    setTutorsAssociation,
    siteAssociation,
    facultiesAssociation,
    roomAssociation,
    tutorsAssociation,
    openAssociations,
    hasDifferentDuration,
    savedInputForm,
    savedSessions,
  } = useAddEditPhysicalEdition(
    trigger,
    id,
    dataLocation,
    mutate,
    activityDuration,
  );

  useEffect(() => {
    if (siteAssociation.length === 0) {
      const sitesCodeIndex = columns.findIndex(
        (item) => item.accessorKey === "rooms",
      );
      const sitesCodeObject = { ...columns[sitesCodeIndex], disabled: true };
      columns[sitesCodeIndex] = sitesCodeObject;
      setEditColumns(columns);
    } else {
      setEditColumns(columns);
    }
  }, [siteAssociation]);

  useEffect(() => {
    if (hasDifferentDuration) {
      setShowModalWarning(true);
    }
  }, [hasDifferentDuration]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-editions-physical.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("add.hero-title")}
          description={t("add.hero-description")}
        />
        <Box sx={{ mb: "2rem" }}>
          <TeamsLimitAlertCst corporateId={id} />
        </Box>
        {columns && (!locationState || !isLoadingActivity) && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <EditionFormCst
              testIdPrefix="edition-physical-add-form"
              row={
                locationState
                  ? {
                      learning_objects: {
                        id: locationState.id,
                        label: locationState.title,
                      },
                      max_capacity: activityDetail?.max_partecipant,
                    }
                  : {}
              }
              columns={editColumns}
              sessionColumns={sessionColumns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleDelete={(accessorKey, el) => handleDelete(accessorKey, el)}
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              association={openAssociations}
              resetSessionAssociations={resetSessionAssociations}
              siteAssociation={siteAssociation}
              facultiesAssociation={facultiesAssociation}
              roomAssociation={roomAssociation}
              tutorsAssociation={tutorsAssociation}
              setSiteAssociation={setSiteAssociation}
              setRoomAssociation={setRoomAssociation}
              setFacultiesAssociation={setFacultiesAssociation}
              setTutorsAssociation={setTutorsAssociation}
              setActivityDuration={setActivityDuration}
            />
          </Box>
        )}

        <ModalConfirmsCst
          open={!!openConfirmStatus}
          title={t("outcome-item", { ns: "modal" })}
          description={t("outcome-item", { ns: "modal" })}
          onCallBackClosed={() => setOpenConfirmStatus(undefined)}
          status={openConfirmStatus}
        />
        <ModalConfirmsCst
          open={showModalWarning}
          title={t("duration-warning.title")}
          description={t("duration-warning.description")}
          routeToSamePage={true}
          onActionConfirmed={() => {
            setShowModalWarning(false);
            savedSessions && onSubmit(savedInputForm, savedSessions, true);
          }}
          onCallBackClosed={() => {
            setShowModalWarning(false);
          }}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default AddPhysicalEditions;
